import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    animate,
    animateChild,
    group,
    query,
    state,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { StateDeclaration, Transition, UIRouter } from '@uirouter/angular';
import { take } from 'rxjs/operators';
import { RouterService } from '@core/state/router/router.service';

@Component({
    selector: 'ihd-modal',
    templateUrl: './modal.component.html',
    animations: [
        trigger('fadeOverlay', [
            transition('void => enter', [
                style({ opacity: 0.5 }),
                group([
                    animate('200ms ease-out', style({ opacity: 1 })),
                    query('.c-modal__container', animateChild()),
                ]),
            ]),
            transition('enter => exit', [
                style({ opacity: 1 }),
                group([
                    query('.c-modal__container', animateChild()),
                    animate('200ms ease-out', style({ opacity: 0 })),
                ]),
            ]),
        ]),
        trigger('slideModal', [
            // Note: The `enter` animation doesn't transition to something like `translate3d(0, 0, 0)
            // scale(1)`, because for some reason specifying the transform explicitly, causes IE both
            // to blur the dialog content and decimate the animation performance. Leaving it as `none`
            // solves both issues.
            state('enter', style({ transform: 'none', opacity: 1 })),
            state(
                'void',
                style({
                    transform: 'translate3d(0, 5%, 0) scale(0.9)',
                    opacity: 0,
                })
            ),
            state(
                'exit',
                style({ transform: 'translate3d(0, 5%, 0)', opacity: 0 })
            ),
            transition(
                '*<=>*',
                animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')
            ),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'c-modal',
    },
})
export class ModalComponent implements OnInit, OnDestroy {
    @Input()
    createOverlay?: boolean = true;

    @Input()
    backdrop?: boolean = true;

    @Output()
    public animationComplete: EventEmitter<any> = new EventEmitter<any>();

    onAnimationEvent(event: any) {
        if (
            event.toState === 'enter' &&
            event.triggerName === 'slideModal' &&
            event.phaseName === 'done'
        ) {
            this.animationComplete.emit();
        }
    }

    public animationState: 'void' | 'enter' | 'exit' = 'enter';

    private unbindBeforeHandler;

    constructor(
        private router: UIRouter,
        private appRouterService: RouterService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        let currentStateName: string;

        this.appRouterService
            .getCurrentState()
            .pipe(take(1))
            .subscribe((currentState: any) => {
                currentStateName = currentState?.name;
            });

        this.unbindBeforeHandler = this.router.transitionService.onBefore(
            {
                from: currentStateName || 'invalidState', // just kill this hook by providing invalid state name if we can't find the currentState
                to: (toState) =>
                    toState
                        ? toState.name.length < currentStateName.length
                        : false,
            },
            (routerTransition: Transition) => {
                const to: StateDeclaration = routerTransition.to();
                const from: StateDeclaration = routerTransition.from();

                // when closing down the modal, we want to show the exit animation
                // so just listening to states when moving to parent
                return new Promise((resolve) => {
                    // listening to states when moving to a parent view, only then we want to animate
                    // if moving to a sibling or opening another modal to a child state, we don't want to animate
                    if (to.name.length < from.name.length && !to.redirectTo) {
                        this.animationState = 'exit';
                        this.cdr.markForCheck();

                        // 300ms is the average animation time for the exit state
                        setTimeout(() => {
                            resolve(true);
                        }, 300);
                    } else {
                        resolve(true);
                    }
                });
            }
        );
    }

    ngOnDestroy() {
        if (this.unbindBeforeHandler) {
            this.unbindBeforeHandler();
        }
    }
}
